/**
 * Group Validation Schemas
 * @package @comp-sys/shared
 */
import { z } from 'zod';
/**
 * Matching condition for school type
 */
const SchoolTypeConditionSchema = z.object({
    field: z.literal('schoolType'),
    operator: z.literal('IN'),
    values: z.array(z.string().min(1)),
});
/**
 * Matching condition for grade range
 */
const GradeConditionSchema = z.object({
    field: z.literal('grade'),
    operator: z.literal('BETWEEN'),
    min: z.number().int().nonnegative(),
    max: z.number().int().nonnegative(),
});
/**
 * Matching condition for age range
 */
const AgeConditionSchema = z.object({
    field: z.literal('age'),
    operator: z.literal('BETWEEN'),
    min: z.number().int().nonnegative(),
    max: z.number().int().nonnegative(),
});
/**
 * Discriminated union of all matching conditions
 */
export const GroupMatchingConditionSchema = z.discriminatedUnion('field', [
    SchoolTypeConditionSchema,
    GradeConditionSchema,
    AgeConditionSchema,
]);
/**
 * Group matching rules schema
 * Validates that grade/age conditions have min <= max
 */
export const GroupMatchingRulesSchema = z.object({
    conditions: z
        .array(GroupMatchingConditionSchema)
        .min(1)
        .refine((conditions) => {
        for (const c of conditions) {
            if (c.field === 'grade' || c.field === 'age') {
                if (c.min > c.max)
                    return false;
            }
        }
        return true;
    }, { message: 'min must be less than or equal to max for range conditions' }),
    logic: z.literal('AND'),
});
/**
 * Certificate number segment schema
 */
export const CertNoSegmentSchema = z.object({
    type: z.enum(['COMPETITION_CODE', 'GROUP_CODE', 'SEQUENCE', 'CUSTOM_TEXT', 'SEPARATOR']),
    value: z.string().optional(),
});
/**
 * Certificate number sequence config schema
 */
export const CertNoSeqConfigSchema = z.object({
    startFrom: z.number().int().nonnegative().default(1),
    paddingDigits: z.number().int().min(1).max(10).default(4),
    perGroup: z.boolean().default(true),
    groupLetterPrefix: z.boolean().default(false),
});
/**
 * Certificate number template schema
 * Must contain at least one SEQUENCE segment
 */
export const CertNoTemplateSchema = z.object({
    segments: z.array(CertNoSegmentSchema).min(1),
    seqConfig: CertNoSeqConfigSchema,
}).refine((data) => data.segments.some((s) => s.type === 'SEQUENCE'), {
    message: 'CertNo template must contain at least one SEQUENCE segment',
});
/**
 * Create group request schema
 */
export const CreateGroupSchema = z.object({
    name: z.string().min(1).max(100),
    code: z.string().regex(/^[A-Z0-9_]+$/, 'Code must contain only uppercase letters, digits, and underscores'),
    description: z.string().max(500).optional(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Color must be a valid hex code').optional(),
    matchingRules: GroupMatchingRulesSchema.optional(),
});
/**
 * Update group request schema (all fields optional)
 */
export const UpdateGroupSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    code: z.string().regex(/^[A-Z0-9_]+$/, 'Code must contain only uppercase letters, digits, and underscores').optional(),
    description: z.string().max(500).optional(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Color must be a valid hex code').optional(),
    matchingRules: GroupMatchingRulesSchema.optional(),
    order: z.number().int().nonnegative().optional(),
    useCustomStages: z.boolean().optional(),
    useCustomRanking: z.boolean().optional(),
    useCustomAwards: z.boolean().optional(),
    useCustomTeams: z.boolean().optional(),
    teamSettings: z.record(z.unknown()).optional(),
    rankingRules: z.record(z.unknown()).optional(),
    awardSettings: z.record(z.unknown()).optional(),
});
//# sourceMappingURL=group.js.map